import {PartOfSpeech, Word} from "../../../../common/types/words";
import {WordAndSynonymEditView} from "./WordAndSynonymEditView";
import React, {forwardRef, useEffect, useImperativeHandle, useState} from "react";
import {AudioType} from "./AudioParamsView";
import {CreateWord, UpdateWord} from "../../types/word_operations";
import {PosEditView} from "./PosEditView";
import {ExampleSentenceEditView} from "./ExampleSentenceEditView";
import {AudioEditView} from "./AudioEditView";
import {useApiRequest} from "../../../../common/hooks/useApiRequest";
import {Snackbar} from "@mui/material";
import {useDeck} from "../../../deck-management/context/DeckContext";
import {useMutation} from "@tanstack/react-query";


export interface WordInfoEditViewProps {
    existingWord?: Word
    wordConfig: CreateWord | UpdateWord
    onChange: (editedWord: Partial<CreateWord | UpdateWord>) => void
    includeFullGenerationButton: boolean
    onFullGenerationStarted?: () => void
    displayAfterLemmaSlot?: React.ReactNode
}

export interface FormValidationHandle {
    validateForm: () => boolean
}

export interface FocusFormHandle {
    focusInput: () => void
}


export const WordInfoEditView = forwardRef<FormValidationHandle & FocusFormHandle, WordInfoEditViewProps>(({existingWord, wordConfig, onChange, includeFullGenerationButton, onFullGenerationStarted, displayAfterLemmaSlot}, ref) => {

    const {postRequest} = useApiRequest();

    const {currentDeck} = useDeck();

    const [displayGenerationFailedSnackbar, setDisplayGenerationFailedSnackbar] = useState<boolean>(false);

    const lemmaFormRef = React.useRef<FormValidationHandle & FocusFormHandle>(null);
    const translationFormRef = React.useRef<FormValidationHandle>(null);
    const exampleSentenceFormRef = React.useRef<FormValidationHandle>(null);

    useImperativeHandle(ref, () => ({
        validateForm,
        focusInput
    }));

    const {mutate: generate, isPending, isError} = useMutation({
        mutationFn: () => {
            return postRequest<Word>('word-properties/generate/single', {
                words: {word: wordConfig.lemma},
                sourceLanguage: currentDeck!.language,
                fieldSelection: {
                    translations: true,
                    exampleSentence: true,
                    pos: true,
                    audio: true,
                    sentenceAudio: true
                },
            })
        },
        onMutate: () => onFullGenerationStarted?.(),
        onSuccess: ({lemma, translations, exampleSentences, partOfSpeech, resources}: Word) => {
            onChange({
                lemma: lemma,
                translations: translations,
                exampleSentences: [{
                    sentence: exampleSentences[0].sentence,
                    translation: exampleSentences[0].translation,
                    audio: resources?.sentence ? {fromPath: resources.sentence} : null
                }],
                partOfSpeech: partOfSpeech || PartOfSpeech.Unknown,
                audio: resources?.lemma ? {fromPath: resources.lemma} : null
            });
        }
    });

    useEffect(() => {
        if (isError) {
            setDisplayGenerationFailedSnackbar(true);
        }
    }, [isError]);

    const validateForm = (): boolean => {
        const lemmaValid = lemmaFormRef.current!.validateForm();
        const translationValid = translationFormRef.current!.validateForm();
        const exampleSentenceValid = exampleSentenceFormRef.current!.validateForm();
        return lemmaValid && translationValid && exampleSentenceValid;
    };

    const focusInput = () => lemmaFormRef.current!.focusInput();


    return (
        <>
            <WordAndSynonymEditView title={currentDeck!.language}
                                    values={[wordConfig.lemma]}
                                    onChange={([newLemma]) => onChange({lemma: newLemma})}
                                    allowSynonyms={false}
                                    spellCheck={false}
                                    autoFocus={true}
                                    isLoading={false}
                                    onGenerationButtonClick={includeFullGenerationButton ? generate : undefined}
                                    generationButtonLoading={isPending}
                                    additionalDisplaySlot={displayAfterLemmaSlot}
                                    ref={lemmaFormRef}
            />
            <WordAndSynonymEditView title={"Translation"}
                                    values={wordConfig.translations}
                                    onChange={(newTranslations) => onChange({translations: newTranslations})}
                                    spellCheck={true}
                                    autoFocus={false}
                                    isLoading={isPending}
                                    ref={translationFormRef}
            />
            <PosEditView currentPos={wordConfig.partOfSpeech}
                         onPosChange={(newPos) => onChange({partOfSpeech: newPos})}
                         isLoading={isPending}
            />
            <AudioEditView type={AudioType.Word}
                           text={wordConfig.lemma}
                           existingAudioPath={existingWord?.resources?.lemma}
                           audioConfig={wordConfig.audio}
                           onChange={(newAudioConfig) => onChange({audio: newAudioConfig})}
                           isLoading={isPending}
            />
            <ExampleSentenceEditView
                existingAudioPath={existingWord?.resources?.sentence}
                sentencesConfig={wordConfig.exampleSentences}
                onChange={updatedSentence => onChange({exampleSentences: updatedSentence})}
                isLoading={isPending}
                ref={exampleSentenceFormRef}
            />
            <Snackbar
                open={displayGenerationFailedSnackbar}
                autoHideDuration={2000}
                onClose={() => setDisplayGenerationFailedSnackbar(false)}
                message={"Failed to fetch word data"}
                anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
                ContentProps={{
                    sx: {
                        color: "white"
                    }
                }}
            />
        </>
    );


});